/**
 * DemoFeedbackWidget — the floating "Feedback" button + popover for prospect
 * (demo) sessions. Demo-gated by the parent (DemoFeedbackChrome); the API enforces the
 * same gate server-side (POST /demo/feedback is demo-sessions-only).
 *
 * Placement: bottom-right, but the bottom-right of the *content*, not blindly
 * of the viewport — the chat/agent panel owns the viewport's bottom-right on
 * most surfaces. We reuse the same signals the layout uses (chat-panel state +
 * rail-route detection + the layout breakpoints from routes/_app.tsx) to nudge
 * the button left of an open right-hand panel, and up above the narrow-layout
 * assistant FAB. Surfaces that pin their own action bar to the bottom of the
 * content — the artifact panel's footer and its Generate/Download CTA —
 * publish that bar's height as `--app-bottom-bar-h`, which we add to the
 * bottom offset so the pill sits above it rather than on top of it. When the
 * chat is maximised the button hides (the whole area is the composer).
 */

import { useEffect, useRef, useState } from "react";

import { useLocation } from "@tanstack/react-router";
import { toast } from "sonner";

import { useApi } from "@/api";
import { usePlanMode } from "@/components/plan/usePlanMode";
import { useActiveThread } from "@/lib/active-thread";
import { useChatPanel } from "@/lib/chat-panel";

import {
  DEMO_BADGE,
  DEMO_GHOST_BTN,
  DEMO_PANEL,
  DEMO_PRIMARY_BTN,
  demoChromeVars,
} from "./chromeTheme";

// Keep in sync with routes/_app.tsx: rail split ≥1024, chat split ≥1100.
const RAIL_SPLIT_MIN_PX = 1024;
const CHAT_SPLIT_MIN_PX = 1100;
const MSG_MAX = 1800;
const GOAL_MAX = 150;

function useViewportWidth(): number {
  const [vw, setVw] = useState(() => (typeof window === "undefined" ? 1280 : window.innerWidth));
  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return vw;
}

export function DemoFeedbackWidget() {
  const api = useApi();
  const { pathname } = useLocation();
  const planMode = usePlanMode();
  const { available, open: chatOpen, maximised, sizePct } = useChatPanel();
  const { workspaceId } = useActiveThread();
  const vw = useViewportWidth();

  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [goal, setGoal] = useState("");
  const [sending, setSending] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Focus the message box when the popover opens (ref-driven so reopening
  // with kept text lands the caret at the end).
  useEffect(() => {
    if (!open) return;
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, [open]);

  // Close on outside click (same pattern as ViewSwitcher).
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Which right-hand surface owns the viewport's bottom-right on this route?
  const railRoute =
    planMode === "plan" || /^\/workspaces\/?$/.test(pathname) || /^\/w\/[^/]+\/?$/.test(pathname);
  const chatSplit = !railRoute && available && chatOpen && vw >= CHAT_SPLIT_MIN_PX;
  const railSplit = railRoute && vw >= RAIL_SPLIT_MIN_PX;
  const panelVisible = chatSplit || railSplit;

  // The chat panel in maximised mode covers the whole content area — nowhere
  // sensible for the button to live.
  if (!railRoute && available && chatOpen && maximised) return null;

  // sizePct is a share of the workspace area; using the viewport overshoots a
  // little (by the sidebar's share), which errs to the safe side — the button
  // lands inside the content, never on the panel.
  const rightPx = panelVisible ? Math.round((vw * sizePct) / 100) + 16 : 16;
  // Narrow rail layouts park an assistant FAB at right-4 bottom-4 — stack above.
  const bottomPx = railRoute && !railSplit ? 76 : 16;
  // A surface with a bottom-pinned action bar (the artifact panel's footer,
  // whose Generate/Download CTA sits in this exact corner) publishes the bar's
  // measured height as `--app-bottom-bar-h`; everywhere else the property is
  // unset and the 0px fallback leaves the button where it has always been.
  // Adding it here keeps the pill clear of the bar at whatever height it
  // renders, instead of fighting the button for the corner with a z-index.
  const bottomCss = `calc(${bottomPx}px + var(--app-bottom-bar-h, 0px))`;

  const submit = async () => {
    const base = message.trim();
    if (!base || sending) return;
    const trying = goal.trim();
    const full = trying ? `${base}\n\n(Trying to: ${trying})` : base;
    setSending(true);
    try {
      await api.submitDemoFeedback({
        message: full,
        context: {
          path: pathname,
          ...(workspaceId ? { workspace_id: workspaceId } : {}),
        },
      });
      toast.success("Thanks — your note went straight to the team.");
      setMessage("");
      setGoal("");
      setOpen(false);
    } catch {
      // Non-blocking: keep the text so nothing typed is lost.
      toast.error("Couldn't send right now — please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    // z-40: above page content (the narrow-rail FAB is z-20) but UNDER the
    // app's Sheet/Dialog overlays (z-50), so modal surfaces cover the button.
    <div
      ref={rootRef}
      className="fixed z-40"
      style={{ ...demoChromeVars, right: rightPx, bottom: bottomCss }}
    >
      {open && (
        <div
          className={`absolute right-0 bottom-full mb-2 w-80 max-w-[calc(100vw-32px)] p-3.5 ${DEMO_PANEL}`}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.stopPropagation();
              setOpen(false);
            } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void submit();
            }
          }}
        >
          <span className={DEMO_BADGE}>Demo guide</span>
          <div className="mt-1.5 text-sm font-semibold">Tell us what you think</div>
          <p className="mt-0.5 text-[12px] text-[var(--demo-muted)]">
            Goes straight to the team preparing your demo.
          </p>
          {/* The two fields stay dark too: they belong to the narrator's
              popover, not to the app. `placeholder:` is set explicitly because
              the UA default is tuned for a light field. */}
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={MSG_MAX}
            rows={4}
            placeholder="What worked? What's missing? What surprised you?"
            className="mt-2.5 w-full resize-none rounded-md border border-[var(--demo-border)] bg-[var(--demo-raised)] p-2 text-[13px] leading-relaxed text-[var(--demo-text)] outline-none placeholder:text-[var(--demo-muted)] focus:border-[var(--demo-accent)]"
          />
          <input
            type="text"
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            maxLength={GOAL_MAX}
            placeholder="What were you trying to do? (optional)"
            className="mt-2 w-full rounded-md border border-[var(--demo-border)] bg-[var(--demo-raised)] px-2 py-1.5 text-[12.5px] text-[var(--demo-text)] outline-none placeholder:text-[var(--demo-muted)] focus:border-[var(--demo-accent)]"
          />
          <div className="mt-2.5 flex items-center justify-end gap-2">
            <button type="button" className={DEMO_GHOST_BTN} onClick={() => setOpen(false)}>
              Cancel
            </button>
            <button
              type="button"
              className={DEMO_PRIMARY_BTN}
              disabled={!message.trim() || sending}
              onClick={() => void submit()}
            >
              {sending ? "Sending…" : "Send"}
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        data-tour="feedback"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex items-center gap-1.5 rounded-full border border-[var(--demo-border-strong)] bg-[var(--demo-surface)] px-3.5 py-2 text-[13px] font-medium text-[var(--demo-text)] shadow-[0_10px_30px_-8px_rgba(0,0,0,0.6)] transition-colors hover:border-[var(--demo-accent)] hover:text-[var(--demo-accent)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--demo-ring)]"
      >
        <MessageIcon />
        Feedback
      </button>
    </div>
  );
}

function MessageIcon() {
  // Inline (lucide MessageSquarePlus geometry) to keep the widget self-contained.
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <path d="M12 7v6" />
      <path d="M9 10h6" />
    </svg>
  );
}
